<template>
    <Head title="IT Equipment" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-3">
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">IT Equipment</h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Manage and track your organization's IT equipment and user assignments</p>
                    </div>
                    <InfoButton title="IT Equipment Management" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The IT Equipment page is your central hub for managing all IT equipment in your organization.
                            Track computers, laptops, monitors, printers, networking equipment, and other IT assets.
                            Equipment can be assigned to multiple users with different roles (primary user, secondary user, admin).
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>View Modes:</strong> Switch between Grid view (visual cards) and List view (table format) using the toggle buttons<br>
                            • <strong>Search:</strong> Type equipment number or name in the search box to quickly find specific items<br>
                            • <strong>Filter:</strong> Use Status, Category, and Location dropdowns to narrow down the equipment list<br>
                            • <strong>Quick Stats:</strong> Dashboard cards show Total Equipment, Active count, Maintenance items, and Retired status<br>
                            • <strong>Create Equipment:</strong> Click "New Equipment" button to register new IT equipment<br>
                            • <strong>Manage Categories:</strong> Click "Manage Categories" to organize equipment classification system<br>
                            • <strong>Equipment Details:</strong> Click any equipment card or row to view complete details, user assignments, and history
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • Use the <strong>Status filter</strong> to focus on Under Repair or Retired equipment<br>
                            • <strong>User Assignments</strong> show who is using each equipment - essential for IT audits<br>
                            • <strong>Primary User</strong> designation helps identify main point of contact for each device<br>
                            • <strong>Transfer feature</strong> makes it easy to reassign equipment between users and locations<br>
                            • Keep <strong>serial numbers and warranty info</strong> up to date for insurance and support purposes
                        </p>
                    </InfoButton>
                </div>
                <div class="flex items-center space-x-3">
                    <Link
                        :href="route('it-equipment-categories.index')"
                        class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-800 transition"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        Manage Categories
                    </Link>
                    <Link
                        v-if="canCreate"
                        :href="route('it-equipment.create')"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-800 transition"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        New Equipment
                    </Link>
                </div>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Total Equipment</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ equipment.total }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Active</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ statusCounts.active || 0 }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-yellow-100 dark:bg-yellow-900/30 rounded-md p-3">
                            <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Maintenance</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ statusCounts.maintenance || 0 }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-gray-100 dark:bg-secondary-700 rounded-md p-3">
                            <svg class="w-6 h-6 text-gray-600 dark:text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Retired</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ statusCounts.retired || 0 }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filters and Search -->
            <div class="bg-white dark:bg-secondary-800 rounded-lg shadow mb-6">
                <div class="p-4 border-b border-gray-200 dark:border-secondary-700">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <!-- Search -->
                        <div class="flex-1 max-w-lg">
                            <div class="relative">
                                <input
                                    v-model="searchQuery"
                                    type="text"
                                    placeholder="Search by equipment number or name..."
                                    class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 focus:ring-blue-500 focus:border-blue-500"
                                    @input="handleSearch"
                                />
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg class="h-5 w-5 text-gray-400 dark:text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- View Toggle -->
                        <div class="flex items-center space-x-2">
                            <button
                                @click="viewMode = 'grid'"
                                :class="[
                                    'p-2 rounded-md',
                                    viewMode === 'grid'
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                        : 'text-gray-400 dark:text-secondary-500 hover:text-gray-600 dark:hover:text-secondary-300'
                                ]"
                            >
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M10 3H4a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1V4a1 1 0 00-1-1zM20 3h-6a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1V4a1 1 0 00-1-1zM10 13H4a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1v-6a1 1 0 00-1-1zM20 13h-6a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1v-6a1 1 0 00-1-1z" />
                                </svg>
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :class="[
                                    'p-2 rounded-md',
                                    viewMode === 'list'
                                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                        : 'text-gray-400 dark:text-secondary-500 hover:text-gray-600 dark:hover:text-secondary-300'
                                ]"
                            >
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Filter Pills -->
                <div class="p-4 flex flex-wrap gap-2">
                    <select
                        v-model="selectedFilters.status"
                        class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="maintenance">Under Maintenance</option>
                        <option value="retired">Retired</option>
                        <option value="disposed">Disposed</option>
                    </select>

                    <select
                        v-model="selectedFilters.category_id"
                        class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    >
                        <option value="">All Categories</option>
                        <option v-for="category in categories" :key="category.id" :value="category.id">
                            {{ category.name }}
                        </option>
                    </select>

                    <select
                        v-model="selectedFilters.location_id"
                        class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        @change="applyFilters"
                    >
                        <option value="">All Locations</option>
                        <option v-for="location in locations" :key="location.id" :value="location.id">
                            {{ location.name }}
                        </option>
                    </select>

                    <button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        class="px-3 py-1.5 text-sm text-gray-600 dark:text-secondary-300 hover:text-gray-800 dark:hover:text-white border border-gray-300 dark:border-secondary-600 rounded-md"
                    >
                        Clear Filters
                    </button>
                </div>

                <!-- Grid View -->
                <div v-if="viewMode === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                    <Link
                        v-for="item in equipment.data"
                        :key="item.id"
                        :href="route('it-equipment.show', item.id)"
                        class="block bg-white dark:bg-secondary-750 border border-gray-200 dark:border-secondary-700 rounded-lg hover:shadow-lg transition-shadow"
                    >
                        <div class="p-4">
                            <div class="flex items-start justify-between mb-2">
                                <div class="flex-1">
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ item.name }}</h3>
                                    <p class="text-sm text-gray-600 dark:text-secondary-400">{{ item.equipment_number }}</p>
                                </div>
                                <span
                                    :class="{
                                        'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400': item.status === 'active',
                                        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400': item.status === 'maintenance',
                                        'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300': item.status === 'retired',
                                        'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': item.status === 'disposed',
                                    }"
                                    class="px-2 py-1 text-xs font-medium rounded-full"
                                >
                                    {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
                                </span>
                            </div>

                            <div class="space-y-2 text-sm text-gray-600 dark:text-secondary-400">
                                <div v-if="item.category" class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                    {{ item.category.name }}
                                </div>

                                <div v-if="item.location" class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {{ item.location.full_path }}
                                </div>

                                <div v-if="item.primary_user" class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    {{ item.primary_user.name }}
                                </div>

                                <div v-if="item.users_count > 0" class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    {{ item.users_count }} user(s) assigned
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>

                <!-- List View -->
                <div v-else class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                        <thead class="bg-gray-50 dark:bg-secondary-750">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Equipment</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Category</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Location</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Primary User</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">Users</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                            <tr
                                v-for="item in equipment.data"
                                :key="item.id"
                                @click="router.visit(route('it-equipment.show', item.id))"
                                class="hover:bg-gray-50 dark:hover:bg-secondary-750 cursor-pointer"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ item.name }}</div>
                                    <div class="text-sm text-gray-500 dark:text-secondary-400">{{ item.equipment_number }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-secondary-400">
                                    {{ item.category?.name || '-' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-secondary-400">
                                    {{ item.location?.full_path || '-' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-secondary-400">
                                    {{ item.primary_user?.name || '-' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="{
                                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400': item.status === 'active',
                                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400': item.status === 'maintenance',
                                            'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300': item.status === 'retired',
                                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400': item.status === 'disposed',
                                        }"
                                        class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                                    >
                                        {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-secondary-400">
                                    {{ item.users_count }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Pagination -->
            <div class="bg-white dark:bg-secondary-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-secondary-700 sm:px-6 rounded-lg shadow">
                <div class="flex-1 flex justify-between sm:hidden">
                    <Link
                        v-if="equipment.prev_page_url"
                        :href="equipment.prev_page_url"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-750 hover:bg-gray-50 dark:hover:bg-secondary-700"
                    >
                        Previous
                    </Link>
                    <Link
                        v-if="equipment.next_page_url"
                        :href="equipment.next_page_url"
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-750 hover:bg-gray-50 dark:hover:bg-secondary-700"
                    >
                        Next
                    </Link>
                </div>
                <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p class="text-sm text-gray-700 dark:text-secondary-300">
                            Showing
                            <span class="font-medium">{{ equipment.from }}</span>
                            to
                            <span class="font-medium">{{ equipment.to }}</span>
                            of
                            <span class="font-medium">{{ equipment.total }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                            <template v-for="link in equipment.links" :key="link.label">
                                <Link
                                    v-if="link.url"
                                    :href="link.url"
                                    :class="[
                                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                        link.active
                                            ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-700 text-blue-600 dark:text-blue-400'
                                            : 'bg-white dark:bg-secondary-750 border-gray-300 dark:border-secondary-600 text-gray-500 dark:text-secondary-400 hover:bg-gray-50 dark:hover:bg-secondary-700'
                                    ]"
                                    v-html="link.label"
                                />
                                <span
                                    v-else
                                    :class="[
                                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium cursor-not-allowed opacity-50',
                                        'bg-white dark:bg-secondary-750 border-gray-300 dark:border-secondary-600 text-gray-400 dark:text-secondary-500'
                                    ]"
                                    v-html="link.label"
                                />
                            </template>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="equipment.data.length === 0" class="bg-white dark:bg-secondary-800 rounded-lg shadow p-12 text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No equipment found</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Get started by creating new IT equipment.</p>
                <div class="mt-6">
                    <Link
                        v-if="canCreate"
                        :href="route('it-equipment.create')"
                        class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        New Equipment
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    equipment: Object,
    categories: Array,
    locations: Array,
    statusCounts: Object,
    filters: Object,
    canCreate: {
        type: Boolean,
        required: true,
    },
    canEdit: {
        type: Boolean,
        required: true,
    },
    canDelete: {
        type: Boolean,
        required: true,
    },
    canAssign: {
        type: Boolean,
        required: true,
    },
});

const viewMode = ref('grid');
const searchQuery = ref(props.filters.search || '');
const selectedFilters = ref({
    status: props.filters.status || '',
    category_id: props.filters.category_id || '',
    location_id: props.filters.location_id || '',
});

let searchTimeout = null;

const hasActiveFilters = computed(() => {
    return selectedFilters.value.status ||
           selectedFilters.value.category_id ||
           selectedFilters.value.location_id ||
           searchQuery.value;
});

function handleSearch() {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
}

function applyFilters() {
    router.get(route('it-equipment.index'), {
        search: searchQuery.value,
        ...selectedFilters.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
}

function clearFilters() {
    searchQuery.value = '';
    selectedFilters.value = {
        status: '',
        category_id: '',
        location_id: '',
    };
    applyFilters();
}
</script>
